import { useCallback, useMemo, useState } from 'react';
import { every, sortBy, words } from 'lodash';
import { t } from '@lingui/macro';
import {
    Accordion,
    AccordionSummary,
    Card,
    CardContent,
    List,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@material-ui/core';
import {
    ExpandMore,
    Cloud,
    CompareArrows,
    DeviceHub,
    Notifications,
    Room,
    Router,
    SettingsEthernet,
    Storage,
    WifiTethering,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { NetworkDevice, NPoint2K, VmwareHypervisor, VmwareVm } from '../../icon';
import Search from '../../search/Search';
import PanelRegistry, { PANEL_CATEGORIES } from '../../page-framework/panel/PanelRegistry';
import { useDashboardContext } from '../../page-framework/dashboard/DashboardContext';
import { getLayoutOptions } from '../../page-framework/panel-container/Layout';
import { getLogger } from '../../../utilities/UseLog';
import { useIsIpmDisabled } from '../../../stores/EnvironmentStore';

const log = getLogger('PanelCatalog');

const PANEL_CATEGORY_TITLES = {
    [PANEL_CATEGORIES.SITE]: t`Site Panels`,
    [PANEL_CATEGORIES.BUSINESS_SERVICE]: t`Business Service Panels`,
    [PANEL_CATEGORIES.ALERT]: t`Alert Panels`,
    [PANEL_CATEGORIES.SERVER]: t`Server Panels`,
    [PANEL_CATEGORIES.DEVICE]: t`Network Device Panels`,
    [PANEL_CATEGORIES.INTERFACE]: t`Interface Panels`,
    [PANEL_CATEGORIES.WIRELESS_CONTROLLER]: t`Wireless Controller Panels`,
    [PANEL_CATEGORIES.ACCESS_POINT]: t`Access Point Panels`,
    [PANEL_CATEGORIES.RADIO]: t`Radio Panels`,
    [PANEL_CATEGORIES.HYPERVISOR]: t`Hypervisor Panels`,
    [PANEL_CATEGORIES.VIRTUAL_MACHINE]: t`Virtual Machine Panels`,
    [PANEL_CATEGORIES.AGENT]: t`nPoint Panels`,
    [PANEL_CATEGORIES.TEST]: t`Service Test Panels`,
};

const PANEL_CATEGORY_ICONS = {
    [PANEL_CATEGORIES.SITE]: <Room />,
    [PANEL_CATEGORIES.BUSINESS_SERVICE]: <Cloud />,
    [PANEL_CATEGORIES.ALERT]: <Notifications />,
    [PANEL_CATEGORIES.SERVER]: <Storage />,
    [PANEL_CATEGORIES.DEVICE]: <NetworkDevice />,
    [PANEL_CATEGORIES.INTERFACE]: <SettingsEthernet />,
    [PANEL_CATEGORIES.WIRELESS_CONTROLLER]: <DeviceHub />,
    [PANEL_CATEGORIES.ACCESS_POINT]: <Router />,
    [PANEL_CATEGORIES.RADIO]: <WifiTethering />,
    [PANEL_CATEGORIES.HYPERVISOR]: <VmwareHypervisor />,
    [PANEL_CATEGORIES.VIRTUAL_MACHINE]: <VmwareVm />,
    [PANEL_CATEGORIES.AGENT]: <NPoint2K />,
    [PANEL_CATEGORIES.TEST]: <CompareArrows />,
};

const PANEL_CATEGORY_GROUPS = {
    'enterprise-panels': {
        title: t`Enterprise`,
        panelCategories: [PANEL_CATEGORIES.SITE, PANEL_CATEGORIES.BUSINESS_SERVICE, PANEL_CATEGORIES.ALERT],
    },
    'service-delivery-panels': {
        title: t`Service Delivery`,
        panelCategories: [PANEL_CATEGORIES.AGENT, PANEL_CATEGORIES.TEST],
    },
    'infrastructure-panels': {
        title: t`Infrastructure`,
        panelCategories: [PANEL_CATEGORIES.SERVER, PANEL_CATEGORIES.DEVICE, PANEL_CATEGORIES.INTERFACE],
    },
    'wireless-panels': {
        title: t`Wireless Infrastructure`,
        panelCategories: [PANEL_CATEGORIES.WIRELESS_CONTROLLER, PANEL_CATEGORIES.ACCESS_POINT, PANEL_CATEGORIES.RADIO],
    },
    'vmware-panels': {
        title: t`VMWare Infrastructure`,
        panelCategories: [PANEL_CATEGORIES.HYPERVISOR, PANEL_CATEGORIES.VIRTUAL_MACHINE],
    },
};

const useStyles = makeStyles((theme) => ({
    menuTitle: {
        color: theme.palette.text.secondary,
        fontSize: '1.1rem',
        fontWeight: theme.typography.fontWeightMedium,
        cursor: 'default',
    },
    search: {
        margin: theme.spacing(0, 1.5),

        '& .MuiSvgIcon-root': {
            height: '0.75em',
        },
        '& .MuiInputBase-input': {
            fontSize: 14,
            paddingBottom: theme.spacing(0.5),
        },
    },
    list: {
        '& .MuiList-root': {
            paddingBottom: 0,
        },
        '& .MuiListItem-root': {
            padding: `${theme.spacing(0.5)}px 12px ${theme.spacing(0.5)}px 9px`,
            borderLeft: 'none',
            height: 'auto',
        },
        '& .MuiAccordion-root:before': {
            backgroundColor: 'unset',
        },
    },
    panelCategory: {
        '& > .MuiAccordionSummary-root:hover': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    panelGroup: {
        '& .MuiList-root': {
            paddingBottom: 0,
        },
        '& .MuiListItemText-root': {
            color: theme.palette.text.primary,
        },
        '&.MuiAccordion-root:before': {
            backgroundColor: 'unset',
        },
        '& .MuiCollapse-container': {
            backgroundColor: theme.palette.background.default,
        },
        '& .MuiCard-root': {
            margin: theme.spacing(1),
            cursor: 'grab',

            '&:hover': {
                boxShadow: theme.shadows[2],
            },
        },
        '& .MuiCardContent-root:last-child': {
            paddingBottom: theme.spacing(2),
        },
        '& .MuiCardContent-root .MuiTypography-body2': {
            fontWeight: 500,
            fontSize: '0.775rem',
        },
    },
    previewImage: {
        width: '100%',
        paddingTop: theme.spacing(1),
    },
}));

/**
 * Matches on empty searchValue, or if non-empty, matches when ALL of the search words match
 */
function searchMatchesPanel(searchValue, panel) {
    if (!searchValue) {
        return true;
    }
    const searchTerms = words(searchValue);
    return every(searchTerms, (term) => {
        return (
            panel.title.toLowerCase().includes(term.toLowerCase()) ||
            panel.description.toLowerCase().includes(term.toLowerCase())
        );
    });
}

function PanelCategoryGroup({ categoryGroup, searchValue }) {
    const styles = useStyles();
    const [isIpmDisabled] = useIsIpmDisabled();
    const matchingCategories = useMemo(() => {
        return categoryGroup.panelCategories.filter((category) => {
            const matchingCategoryPanels = PanelRegistry.getPanelTypesByCategory(category).filter((panel) => {
                const disabledViaIpm = panel.ipm && isIpmDisabled;
                return !disabledViaIpm && searchMatchesPanel(searchValue, panel);
            });
            return matchingCategoryPanels.length !== 0;
        });
    }, [categoryGroup, searchValue, isIpmDisabled]);

    if (matchingCategories.length === 0) {
        return null;
    }

    return (
        <Accordion className={styles.panelCategory} elevation={0} expanded>
            <AccordionSummary>{categoryGroup.title}</AccordionSummary>
            <List className={styles.root}>
                {matchingCategories.map((panelCategory) => {
                    return <PanelGroup key={panelCategory} category={panelCategory} searchValue={searchValue} />;
                })}
            </List>
        </Accordion>
    );
}

function PanelGroup({ category, searchValue }) {
    const theme = useTheme();
    const styles = useStyles();
    const categoryTitle = PANEL_CATEGORY_TITLES[category] || category;
    const categoryIcon = PANEL_CATEGORY_ICONS[category];
    const [expanded, setExpanded] = useState(false);
    const { dashboardActions } = useDashboardContext();
    const [isIpmDisabled] = useIsIpmDisabled();

    const panels = useMemo(() => {
        const panelsInCategory = sortBy(PanelRegistry.getPanelTypesByCategory(category), [
            (panelType) => panelType.priority,
            (panelType) => panelType.ipm,
            (panelType) => panelType.title,
        ]);
        return panelsInCategory.filter((panel) => {
            const disabledViaIpm = panel.ipm && isIpmDisabled;
            return !disabledViaIpm && searchMatchesPanel(searchValue, panel);
        });
    }, [category, searchValue, isIpmDisabled]);

    function handleDragStart(e, panel) {
        const layoutOptions = getLayoutOptions(
            {
                props: {
                    ...panel.defaultProps,
                    stateSummaryOptions: {
                        show: true,
                    },
                },
            },
            theme,
            false
        );
        const { h, minH } = layoutOptions;

        log.debug(`Dragging panel of type ${panel.typeName}`);
        log.debugTable(layoutOptions);

        dashboardActions.setDragging(true);
        dashboardActions.setDroppingItemHeight(h || minH);
        e.dataTransfer.setData('text/plain', JSON.stringify(panel));
    }

    function handleDragEnd() {
        dashboardActions.setDragging(false);
    }

    if (panels.length === 0) {
        return null;
    }

    return (
        <Accordion
            key={category}
            className={styles.panelGroup}
            elevation={0}
            expanded={expanded || !!searchValue}
            onChange={(_event, _expanded) => setExpanded(_expanded)}
        >
            <AccordionSummary id={`${category}-panels`} expandIcon={searchValue?.length > 0 ? null : <ExpandMore />}>
                <ListItemIcon>{categoryIcon}</ListItemIcon>
                <ListItemText>{categoryTitle}</ListItemText>
            </AccordionSummary>
            <List>
                {panels.map((panel) => (
                    <Card
                        key={panel.typeName}
                        variant="outlined"
                        title={panel.description}
                        unselectable="on"
                        draggable
                        onDragStart={(e) => handleDragStart(e, panel)}
                        onDragEnd={handleDragEnd}
                    >
                        <CardContent>
                            <Typography variant="body2">{panel.title}</Typography>
                            <img
                                className={styles.previewImage}
                                src={panel.previewImage}
                                alt={panel.title}
                                draggable={false}
                            />
                        </CardContent>
                    </Card>
                ))}
            </List>
        </Accordion>
    );
}

export default function PanelCatalog({ grabFocus }) {
    const styles = useStyles();
    const [searchValue, setSearchValue] = useState('');

    const handleSearchChange = useCallback(
        (newSearchValue) => {
            if (newSearchValue !== searchValue) {
                setSearchValue(newSearchValue);
            }
        },
        [searchValue]
    );

    return (
        <>
            <Search className={styles.search} autoFocus={grabFocus} onSearchChange={handleSearchChange} />
            {Object.entries(PANEL_CATEGORY_GROUPS).map(([key, categoryGroup]) => {
                return <PanelCategoryGroup key={key} categoryGroup={categoryGroup} searchValue={searchValue} />;
            })}
        </>
    );
}
